import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '../services/apiService';

interface Permission {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

interface Permissions {
  dashboard: Permission;
  products: Permission;
  invoices: Permission;
  reports: Permission;
  projects: Permission;
  customers: Permission;
  suppliers: Permission;
  employees: Permission;
  users: Permission;
  settings: Permission;
  warranty: Permission;
  categories: Permission;
  brands: Permission;
  locations: Permission;
  productTypes: Permission;
  compressorTypes: Permission;
  countries: Permission;
  units: Permission;
  requisitions: Permission;
}

interface PermissionsContextType {
  permissions: Permissions | null;
  hasPermission: (resource: string, action: string) => boolean;
  refreshPermissions: () => Promise<void>;
  updatePermissions: (newPermissions: Permissions) => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const { user } = useAuth();

  const hasPermission = (resource: string, action: string): boolean => {
    console.log(`Checking permission: ${resource}.${action} for user:`, user?.username, 'role:', user?.role);
    console.log('Current permissions state:', permissions);
    
    if (!user) {
      console.log('No user - returning false');
      return false;
    }
    
    // Admin has all permissions
    if (user.role === 'admin') {
      console.log('Admin user - returning true');
      return true;
    }
    
    // For non-admin users, check specific permissions
    if (!permissions) {
      console.log('No permissions loaded - returning false');
      return false;
    }
    
    const resourcePermissions = permissions[resource as keyof Permissions];
    console.log(`Resource permissions for ${resource}:`, resourcePermissions);
    
    if (!resourcePermissions) {
      console.log(`No permissions found for resource: ${resource}`);
      return false;
    }
    
    const result = resourcePermissions[action as keyof Permission] || false;
    console.log(`Permission result for ${resource}.${action}:`, result);
    return result;
  };

  const refreshPermissions = async () => {
    if (!user) return;
    
    console.log('Refreshing permissions for user:', user.username, 'role:', user.role);
    
    // For admin users, set all permissions to true without API call
    if (user.role === 'admin') {
      const adminPermissions: Permissions = {
        dashboard: { create: true, read: true, update: true, delete: true },
        products: { create: true, read: true, update: true, delete: true },
        invoices: { create: true, read: true, update: true, delete: true },
        reports: { create: true, read: true, update: true, delete: true },
        projects: { create: true, read: true, update: true, delete: true },
        customers: { create: true, read: true, update: true, delete: true },
        suppliers: { create: true, read: true, update: true, delete: true },
        employees: { create: true, read: true, update: true, delete: true },
        users: { create: true, read: true, update: true, delete: true },
        settings: { create: true, read: true, update: true, delete: true },
        warranty: { create: true, read: true, update: true, delete: true },
        categories: { create: true, read: true, update: true, delete: true },
        brands: { create: true, read: true, update: true, delete: true },
        locations: { create: true, read: true, update: true, delete: true },
        productTypes: { create: true, read: true, update: true, delete: true },
        compressorTypes: { create: true, read: true, update: true, delete: true },
        countries: { create: true, read: true, update: true, delete: true },
        units: { create: true, read: true, update: true, delete: true },
        requisitions: { create: true, read: true, update: true, delete: true }
      };
      setPermissions(adminPermissions);
      console.log('Set admin permissions:', adminPermissions);
      return;
    }
    
    // For non-admin users, set default permissions based on role
    if (user.role === 'manager') {
      const managerPermissions: Permissions = {
        dashboard: { create: true, read: true, update: true, delete: true },
        products: { create: true, read: true, update: true, delete: true },
        invoices: { create: true, read: true, update: true, delete: true },
        reports: { create: true, read: true, update: true, delete: true },
        projects: { create: true, read: true, update: true, delete: true },
        customers: { create: true, read: true, update: true, delete: true },
        suppliers: { create: true, read: true, update: true, delete: true },
        employees: { create: true, read: true, update: true, delete: true },
        users: { create: false, read: false, update: false, delete: false },
        settings: { create: false, read: false, update: false, delete: false },
        warranty: { create: false, read: true, update: false, delete: false },
        categories: { create: true, read: true, update: true, delete: true },
        brands: { create: true, read: true, update: true, delete: true },
        locations: { create: true, read: true, update: true, delete: true },
        productTypes: { create: true, read: true, update: true, delete: true },
        compressorTypes: { create: true, read: true, update: true, delete: true },
        countries: { create: true, read: true, update: true, delete: true },
        units: { create: true, read: true, update: true, delete: true },
        requisitions: { create: true, read: true, update: true, delete: true }
      };
      setPermissions(managerPermissions);
      console.log('Set manager permissions:', managerPermissions);
      return;
    }
    
    if (user.role === 'user') {
      const userPermissions: Permissions = {
        dashboard: { create: true, read: true, update: true, delete: true },
        products: { create: true, read: true, update: true, delete: true },
        invoices: { create: true, read: true, update: true, delete: true },
        reports: { create: true, read: true, update: true, delete: true },
        projects: { create: true, read: true, update: true, delete: true },
        customers: { create: true, read: true, update: true, delete: true },
        suppliers: { create: true, read: true, update: true, delete: true },
        employees: { create: true, read: true, update: true, delete: true },
        users: { create: false, read: false, update: false, delete: false },
        settings: { create: false, read: false, update: false, delete: false },
        warranty: { create: false, read: true, update: false, delete: false },
        categories: { create: true, read: true, update: true, delete: true },
        brands: { create: true, read: true, update: true, delete: true },
        locations: { create: true, read: true, update: true, delete: true },
        productTypes: { create: true, read: true, update: true, delete: true },
        compressorTypes: { create: true, read: true, update: true, delete: true },
        countries: { create: true, read: true, update: true, delete: true },
        units: { create: true, read: true, update: true, delete: true },
        requisitions: { create: true, read: true, update: true, delete: true }
      };
      setPermissions(userPermissions);
      console.log('Set user permissions:', userPermissions);
      return;
    }

    // Requisition Users: only Requisitions, Products, Customers, Projects, Other Entities
    if (user.role === 'requisition_user') {
      const requisitionUserPermissions: Permissions = {
        dashboard: { create: false, read: false, update: false, delete: false },
        products: { create: true, read: true, update: true, delete: true },
        invoices: { create: false, read: false, update: false, delete: false },
        reports: { create: false, read: false, update: false, delete: false },
        projects: { create: true, read: true, update: true, delete: true },
        customers: { create: true, read: true, update: true, delete: true },
        suppliers: { create: false, read: false, update: false, delete: false },
        employees: { create: false, read: false, update: false, delete: false },
        users: { create: false, read: false, update: false, delete: false },
        settings: { create: false, read: false, update: false, delete: false },
        warranty: { create: false, read: false, update: false, delete: false },
        categories: { create: true, read: true, update: true, delete: true },
        brands: { create: true, read: true, update: true, delete: true },
        locations: { create: true, read: true, update: true, delete: true },
        productTypes: { create: true, read: true, update: true, delete: true },
        compressorTypes: { create: true, read: true, update: true, delete: true },
        countries: { create: true, read: true, update: true, delete: true },
        units: { create: true, read: true, update: true, delete: true },
        requisitions: { create: true, read: true, update: true, delete: true }
      };
      setPermissions(requisitionUserPermissions);
      console.log('Set requisition_user permissions:', requisitionUserPermissions);
      return;
    }
    
    // Fallback: try to get permissions from server
    try {
      console.log('Fetching permissions for user ID:', user._id);
      const response = await apiService.get(`/users/${user._id}`);
      const userData = response.data;
      
      console.log('Received user data:', userData);
      
      if (userData && userData.permissions) {
        console.log('Setting permissions from server:', userData.permissions);
        setPermissions(userData.permissions);
      } else {
        console.log('No permissions found in user data');
      }
    } catch (error) {
      console.error('Error refreshing permissions:', error);
    }
  };

  const updatePermissions = (newPermissions: Permissions) => {
    setPermissions(newPermissions);
  };

  // Load permissions when user changes
  useEffect(() => {
    if (user) {
      refreshPermissions();
    } else {
      setPermissions(null);
    }
  }, [user]);

  const value: PermissionsContextType = {
    permissions,
    hasPermission,
    refreshPermissions,
    updatePermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}; 